import type { ReactElement } from 'react';
import { NavLink } from 'react-router';
import {
  Activity,
  BadgePercent,
  BarChart3,
  BookOpen,
  FileText,
  Inbox,
  LayoutDashboard,
  Receipt,
  ShieldAlert,
  Users,
  Wallet,
} from 'lucide-react';
import type { UserRole } from '@xdocs/schemas';
import { cn } from '@xdocs/ui';
import type { AdminMessageId } from './useAdminT.js';
import { useAdminT } from './useAdminT.js';
import { roleSatisfies } from './roleRank.js';
import styles from './AdminNav.module.css';

interface AdminNavItem {
  key: string;
  href: string;
  labelKey: AdminMessageId;
  icon: ReactElement;
  /** Minimum role that can see this item. The mockup's `ADMIN_NAV`
   * (design/app/38-admin-overview.html `ADMIN_NAV` const) marks BOTH
   * `assets` and `review-queue` as `roles:['admin','moderator']` — but
   * `packages/schemas/src/admin.ts`'s own header comment freezes every
   * `admin.*` op (including `admin.assets.*`, F60) at server-side
   * `requireRole('admin')`; only `queue.*`/`moderation.takedown` (F74/F69)
   * live in the separately-scoped `moderation.ts` moderator surface. Since
   * the server contract is stricter than the mockup's demo "VIEW AS ROLE"
   * toggle for `assets` specifically, this mirrors the FROZEN CONTRACT
   * (`admin`-only for `assets`, matching `AssetsPage.tsx`'s own
   * `<RoleGate minimum="admin">`) rather than the mockup's preview-only
   * role list — showing a moderator a nav link that 403s on every load
   * would be worse than not showing it. `review-queue` stays
   * moderator-reachable, matching both the mockup AND the schema's
   * moderator-scoped `queue.*` ops. */
  minimum: UserRole;
}

const ITEMS: readonly AdminNavItem[] = [
  { key: 'overview', href: '/admin/overview', labelKey: 'admin:nav.overview', icon: <LayoutDashboard size={16} aria-hidden="true" />, minimum: 'admin' },
  { key: 'users', href: '/admin/users', labelKey: 'admin:nav.users', icon: <Users size={16} aria-hidden="true" />, minimum: 'admin' },
  { key: 'invoices', href: '/admin/invoices', labelKey: 'admin:nav.invoices', icon: <Receipt size={16} aria-hidden="true" />, minimum: 'admin' },
  { key: 'billing', href: '/admin/billing', labelKey: 'admin:nav.billing', icon: <Wallet size={16} aria-hidden="true" />, minimum: 'admin' },
  { key: 'analytics', href: '/admin/analytics', labelKey: 'admin:nav.analytics', icon: <BarChart3 size={16} aria-hidden="true" />, minimum: 'admin' },
  { key: 'inbox', href: '/admin/inbox', labelKey: 'admin:nav.inbox', icon: <Inbox size={16} aria-hidden="true" />, minimum: 'admin' },
  { key: 'assets', href: '/admin/assets', labelKey: 'admin:nav.assets', icon: <FileText size={16} aria-hidden="true" />, minimum: 'admin' },
  { key: 'review-queue', href: '/admin/review-queue', labelKey: 'admin:nav.reviewQueue', icon: <ShieldAlert size={16} aria-hidden="true" />, minimum: 'moderator' },
  { key: 'promo-codes', href: '/admin/promos', labelKey: 'admin:nav.promoCodes', icon: <BadgePercent size={16} aria-hidden="true" />, minimum: 'admin' },
  { key: 'audit', href: '/admin/audit', labelKey: 'admin:nav.audit', icon: <Activity size={16} aria-hidden="true" />, minimum: 'admin' },
  { key: 'landing-cms', href: '/admin/landing-cms', labelKey: 'admin:nav.landingCms', icon: <BookOpen size={16} aria-hidden="true" />, minimum: 'admin' },
];

export interface AdminNavProps {
  role: UserRole | null;
}

/**
 * Shared admin sidebar nav (design/app/38-admin-overview.html's
 * `ADMIN_NAV`), filtered to what `role` can see (admin: all 11;
 * moderator: `review-queue` only — see each item's `minimum` doc comment
 * for why `assets` is admin-only despite the mockup listing it as
 * moderator-reachable too). Every route below is either this task's own
 * (`overview`/`users`/`analytics`) or another M4 builder's slice
 * (`billing`/`promo-codes`/`inbox`/`assets`/`review-queue`/`audit`/
 * `landing-cms`/`invoices`) — this component only renders the links; which
 * ones resolve to a mounted route depends on Integrate wiring every
 * workstream's routes into `App.tsx` (see `WIRING.md`). `invoices` has no
 * mounted route yet — F70's invoices table lives on `/admin/billing`
 * instead (see `billing/WIRING.md`); this link 404s until either the
 * mockup's separate Invoices screen gets its own route or this entry is
 * pointed at `/admin/billing`.
 */
export function AdminNav({ role }: AdminNavProps): ReactElement {
  const { t } = useAdminT();
  const visible = ITEMS.filter((item) => roleSatisfies(role, item.minimum));

  return (
    <nav className={cn(styles['nav'])} aria-label={t('admin:shell.title')}>
      {visible.map((item) => (
        <NavLink key={item.key} to={item.href} className={cn(styles['navItem'])}>
          {item.icon}
          {t(item.labelKey)}
        </NavLink>
      ))}
    </nav>
  );
}
